import React, { createContext, useContext, useEffect, useState } from "react";
import {
    api_get,
    api_post,
    apiError,
    getUserToken,
    setUserToken,
    getAdminToken,
    setAdminToken,
} from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const token = getUserToken();
        if (!token) {
            setLoading(false);
            return;
        }
        api_get("/auth/me")
            .then((u) => {
                if (!cancelled) setUser(u);
            })
            .catch(() => {
                setUserToken(null);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, []);

    const register = async ({ name, email, phone, password }) => {
        try {
            const data = await api_post("/auth/register", { name, email, phone, password });
            setUserToken(data.token);
            setUser(data.user);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: apiError(err) };
        }
    };

    const login = async ({ email, password }) => {
        try {
            const data = await api_post("/auth/login", { email, password });
            setUserToken(data.token);
            setUser(data.user);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: apiError(err) };
        }
    };

    const googleLogin = async () => {
        try {
            const data = await api_post("/auth/google", {});
            setUserToken(data.token);
            setUser(data.user);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: apiError(err) };
        }
    };

    const logout = () => {
        setUserToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, register, login, googleLogin, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};

export const useAdminAuth = () => {
    const [admin, setAdmin] = useState(() =>
        getAdminToken() ? { username: "admin" } : null,
    );

    const adminLogin = async ({ username, password }) => {
        try {
            const data = await api_post("/admin/login", { username, password });
            setAdminToken(data.token);
            setAdmin({ username: data.username });
            return { ok: true };
        } catch (err) {
            return { ok: false, error: apiError(err) };
        }
    };

    const adminLogout = () => {
        setAdminToken(null);
        setAdmin(null);
    };

    return { admin, adminLogin, adminLogout };
};
